import * as Sentry from '@sentry/solid';

export type SentryOptions = {
  dsn?: string;
  tracesSampleRate?: number;
  release?: string;
  environment?: string;
  sampleRate?: number;
};

export function initSentry(opts: SentryOptions = {}) {
  if (!opts.dsn) {
    console.warn('[@bz/sentry] Sentry DSN not provided');
    return;
  }

  // BrowserTracing is optional. Require dynamically so the package scaffold
  // does not force installing @sentry/tracing during initial setup.
  const integrations: any[] = [Sentry.replayIntegration()];
  if (opts.tracesSampleRate && opts.tracesSampleRate > 0) {
    try {
      const { BrowserTracing } = require('@sentry/tracing');
      integrations.push(new BrowserTracing());
    } catch {
      console.warn('[@bz/sentry] @sentry/tracing not installed; skipping tracing integration.');
    }
  }

  Sentry.init({
    dsn: opts.dsn,
    integrations,
    tracesSampleRate: opts.tracesSampleRate ?? 0,
    sampleRate: opts.sampleRate ?? 1,
    release: opts.release,
    environment: opts.environment,
    sendDefaultPii: true,
    // Session Replay
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 1.0,
    // 允许传入其他配置
    ...((opts as any).rest || {}),
  });

  // Global handlers
  window.addEventListener('error', (e: ErrorEvent) => {
    Sentry.captureException(e.error || e.message);
  });

  window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
    Sentry.captureException(e.reason);
  });
}

export function captureException(err: any, context?: Record<string, any>, tags?: Record<string, any>) {
  return Sentry.captureException(err, { extra: context, tags });
}

export function captureMessage(
  msg: string,
  level: 'info' | 'warning' | 'error' = 'info',
  context?: Record<string, any>
) {
  Sentry.captureMessage(msg, { level, extra: context });
}

export function setUser(userId: string | null, emailOrPhone?: string) {
  if (userId === null) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({ id: userId, username: emailOrPhone });
}

export function setTags(tags: Record<string, string>) {
  Sentry.setTags(tags);
}
